import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { PriorityCalculator, PriorityFactors, PriorityScores } from './priorityCalculator'

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8787',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url)
    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Add response interceptor for logging
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('API Response:', response.status, response.config.url)
    return response
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.response?.data)
    return Promise.reject(error)
  }
)

export interface Project {
  id?: number
  client_name: string
  project_common_name?: string
  agreement_status?: string
  start_date?: string
  partner_agreements?: string
  project_status?: string
  timeline_to_resume?: string
  nature_of_work?: string
  relationship_manager?: string
  account_manager?: string
  billed?: string
  monthly_retainer?: number
  months_of_revenue?: number
  expected_fiat_revenue?: number
  token_potential?: number
  billing_health?: number
  tge_date?: string
  priority?: number
  auto?: boolean
  notes?: string
  core_hours?: string
  engagement_style?: string
  legal_notes?: string
  created_at?: string
  updated_at?: string
  // New priority fields
  priority_score?: number
  revenue_score?: number
  health_score?: number
  strategic_score?: number
  token_score?: number
  // New token schedule fields
  next_vest_date?: string
  day1_unlock?: boolean
  material_transaction_amount?: number
  mm_needed_at_tge?: string
  approvers?: string
  wallet_owner?: string
  wallet_chain?: string
  wallet_address?: string
  wallet_ex_platform?: string
  wallet_type?: string
  // Token data flags (API returns 1/0, but we'll handle both boolean and number)
  token_has_vesting_schedule?: boolean | number
  token_has_financial_data?: boolean | number
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://internal-architect-dashboard-react.web-12c.workers.dev'

class ProjectsAPI {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('API request failed:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async getAll(): Promise<ApiResponse<Project[]>> {
    const response = await this.request<Project[]>('/api/projects')
    
    // Calculate priority scores for all projects
    if (response.success && response.data) {
      response.data = response.data.map(project => {
        const priorityScores = this.calculateProjectPriority(project)
        return {
          ...project,
          priority_score: priorityScores?.totalScore,
          revenue_score: priorityScores?.revenueScore,
          health_score: priorityScores?.healthScore,
          strategic_score: priorityScores?.strategicScore,
          token_score: priorityScores?.tokenScore,
        }
      })
    }
    
    return response
  }

  async getById(id: string): Promise<ApiResponse<Project>> {
    const response = await this.request<Project>(`/api/projects/${id}`)
    
    // Calculate priority score for single project
    if (response.success && response.data) {
      const priorityScores = this.calculateProjectPriority(response.data)
      response.data = {
        ...response.data,
        priority_score: priorityScores?.totalScore,
        revenue_score: priorityScores?.revenueScore,
        health_score: priorityScores?.healthScore,
        strategic_score: priorityScores?.strategicScore,
        token_score: priorityScores?.tokenScore,
      }
    }
    
    return response
  }

  async create(project: Project): Promise<ApiResponse<Project>> {
    const response = await this.request<Project>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    })
    
    // Calculate priority score for new project
    if (response.success && response.data) {
      const priorityScores = this.calculateProjectPriority(response.data)
      response.data = {
        ...response.data,
        priority_score: priorityScores?.totalScore,
        revenue_score: priorityScores?.revenueScore,
        health_score: priorityScores?.healthScore,
        strategic_score: priorityScores?.strategicScore,
        token_score: priorityScores?.tokenScore,
      }
    }
    
    return response
  }

  async update(id: string, project: Project): Promise<ApiResponse<Project>> {
    const response = await this.request<Project>(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    })
    
    // Calculate priority score for updated project
    if (response.success && response.data) {
      const priorityScores = this.calculateProjectPriority(response.data)
      response.data = {
        ...response.data,
        priority_score: priorityScores?.totalScore,
        revenue_score: priorityScores?.revenueScore,
        health_score: priorityScores?.healthScore,
        strategic_score: priorityScores?.strategicScore,
        token_score: priorityScores?.tokenScore,
      }
    }
    
    return response
  }

  async delete(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/projects/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Calculate priority scores for a project based on the Excel formula
   */
  private calculateProjectPriority(project: Project): PriorityScores | null {
    // Extract factors from project data
    const factors: PriorityFactors = {
      revenue: project.expected_fiat_revenue || 0,
      health: project.billing_health || 0,
      strategic: this.getStrategicScore(project),
      token: project.token_potential || 0,
    }

    return PriorityCalculator.calculatePriority(factors)
  }

  /**
   * Get strategic score based on project characteristics
   */
  private getStrategicScore(project: Project): number {
    let score = 0
    
    // Base score from project status
    switch (project.project_status) {
      case 'Active': score += 3; break
      case 'Pending': score += 2; break
      case 'Paused': score += 1; break
      case 'Completed': score += 0; break
    }
    
    // Bonus for high-value projects
    if ((project.monthly_retainer || 0) >= 25000) score += 1
    if ((project.expected_fiat_revenue || 0) >= 100000) score += 1
    
    // Bonus for strategic work types
    switch (project.nature_of_work) {
      case 'Multi-discipline': score += 1; break
      case 'Token Design': score += 1; break
      case 'Fundraising': score += 1; break
    }
    
    return Math.min(score, 5) // Cap at 5
  }

  /**
   * Get priority factors for a project
   */
  async getPriorityFactors(projectId: string): Promise<ApiResponse<PriorityFactors>> {
    const project = await this.getById(projectId)
    if (!project.success || !project.data) {
      return { success: false, error: 'Project not found' }
    }

    const factors: PriorityFactors = {
      revenue: project.data.expected_fiat_revenue || 0,
      health: project.data.billing_health || 0,
      strategic: this.getStrategicScore(project.data),
      token: project.data.token_potential || 0,
    }

    return { success: true, data: factors }
  }

  /**
   * Update priority factors for a project
   */
  async updatePriorityFactors(projectId: string, factors: PriorityFactors): Promise<ApiResponse<Project>> {
    const project = await this.getById(projectId)
    if (!project.success || !project.data) {
      return { success: false, error: 'Project not found' }
    }

    // Update project with new factors
    const updatedProject = {
      ...project.data,
      expected_fiat_revenue: factors.revenue,
      billing_health: factors.health,
      token_potential: factors.token,
      // Strategic score is calculated, not stored directly
    }

    return this.update(projectId, updatedProject)
  }
}

export const projectsAPI = new ProjectsAPI()

export const dashboardAPI = {
  getDashboard: async () => {
    const response = await api.get<ApiResponse<any>>('/api/dashboard')
    return response.data
  },
}
  
export const analyticsAPI = {
  getAnalytics: async () => {
    const response = await api.get<ApiResponse<any>>('/api/analytics')
    return response.data
  },
}